import React from 'react';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@oysterjs/ui/dialog';
import { FormError } from '~/components/form-error';

/**
 * Shared confirm dialog used by library list pages (and outbound Calls bulk
 * cancel). Defaults match archive copy; callers can override title / body /
 * labels for other destructive bulk actions while keeping the same chrome.
 */
export function ArchiveConfirmDialog(props: {
  open: boolean;
  count: number;
  /** Singular noun for the item; the dialog appends 's' for plural. */
  itemNoun: string;
  /** Optional plural override when the noun isn't a simple +s. */
  itemPlural?: string;
  /** Extra copy after the standard "removed from the list" sentence. */
  notice?: string;
  /** Overrides the default "Archive {plural}" title. */
  title?: string;
  /** Overrides the default dismiss button label ("Cancel"). */
  dismissLabel?: string;
  /** Overrides the default confirm button label ("Archive"). */
  confirmLabel?: string;
  /** Replaces the default archive description when set. */
  description?: React.ReactNode;
  /** Why the last confirm failed, shown above the buttons so the user can try again. */
  error?: string | null;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const plural = props.itemPlural ?? `${props.itemNoun}s`;
  const noun = props.count === 1 ? props.itemNoun : plural;
  const title = props.title ?? `Archive ${plural}`;
  const dismissLabel = props.dismissLabel ?? 'Cancel';
  const confirmLabel = props.confirmLabel ?? 'Archive';

  return (
    <Dialog open={props.open} onClose={props.onCancel} size="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>
        {props.description ?? (
          <>
            Are you sure you want to archive <strong>{props.count}</strong> {noun}? They will be
            removed from the list and Cara will no longer be able to find or use them for new
            documents.
            {props.notice ? ` ${props.notice}` : ''}
          </>
        )}
      </DialogDescription>
      <FormError message={props.error} className="mt-4 text-sm text-red-600 dark:text-red-400" />
      <DialogActions>
        <Button type="button" color="white" onClick={props.onCancel} disabled={!!props.loading}>
          {dismissLabel}
        </Button>
        <Button
          type="button"
          color="sky"
          onClick={props.onConfirm}
          loading={!!props.loading}
          disabled={props.count <= 0}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
